import { Injectable } from '@angular/core';
import {
  AbstractControl,
  UntypedFormBuilder, UntypedFormGroup,
  ValidationErrors,
  Validators
} from '@angular/forms';
import { Observable, of, timer } from 'rxjs';
import {
  catchError, first,
  map,
  switchMap,
  tap
} from 'rxjs/operators';
import { IRedeemedInsuranceResponse, RedemptionCodesHttpService } from '@sondermind/data-access/redemption';
import { FormControls, PartialForm } from '@sondermind/utilities/forms';

export interface RedemptionCodeFormData {
  redemption_code: string;
}

@Injectable()
export class RedemptionCodeForm extends PartialForm<RedemptionCodeFormData, IRedeemedInsuranceResponse> {
  grantorName: string | null = null;
  redemptionsGranted: number | null = null;

  constructor(
    private builder: UntypedFormBuilder,
    private codesSvc: RedemptionCodesHttpService
  ) {
    super();
  }

  build(): UntypedFormGroup {
    // eslint-disable-next-line prefer-destructuring
    const required = Validators.required;
    const codeValidator = (c: AbstractControl) => this.validateCode(c.value);

    return this.builder.group(<FormControls<RedemptionCodeFormData>>{
      redemption_code: this.builder.control(null, [required], [codeValidator])
    });
  }

  save(value: RedemptionCodeFormData): Observable<IRedeemedInsuranceResponse> {
    return this.codesSvc.redeem(value.redemption_code).pipe(
      this.markSuccessfulSave(),
      this.applyErrorsToForm()
    );
  }

  /*
    validate that the code is good (and can be applied to this client)

    - blank codes are fine, since that's a separate required validator
    - possible errors:
      - unknown code from the backend, http 404, { notFound: true }
      - already redeemed code, http 403, { notAvailable: true }
      - error checking redemption code, http 4xx, 5xx { serverError: true}

    additionally, if the validator passes, we'll make the redemption units
    and grantor available in the form, so that they can be presented
   */
  private validateCode(value: string, debounceMs: number = 500): Observable<ValidationErrors> {
    const code = `${value || ''}`.trim();
    if (code === '') {
      this.redemptionsGranted = null;
      this.grantorName = null;

      return of(null);
    }

    // timer gives us debouncing:

    // we can't use debounceTime, because the validate method returns different
    // observables each time, rather than letting the response emit on the same
    // observable, so it would debounce the invalid instance, but generate a new
    // one on each (change)

    // rather, use timer so that the form control will unsubscribe before the timer
    // fires, cancelling that validation request altogether, and we get a new one
    // that's also behind a timer, effectively debouncing input before hitting the
    // htttp service.

    // ask the fetch service not to return null on 404, since we want the error to flow

    return timer(debounceMs).pipe(
      tap(() => {
        this.redemptionsGranted = null;
        this.grantorName = null;
      }),
      switchMap(() => this.codesSvc.fetch(code, { error: { null404: false } })),
      map((resp) => {
        // already redeemed, by _this_ user
        if (resp.data.is_redeemed) {
          throw { status: 403 };
        }

        // otherwise, it's good
        this.grantorName = resp.data.grantor_name;
        this.redemptionsGranted = resp.data.redemptions_granted;

        return null;
      }),
      catchError((e) => {
        if (e.status === 404) {
          return of({ notFound: true });
        }

        if (e.status === 403) {
          return of({ notAvailable: true });
        }

        if (e.status === 429) {
          return of({ rateLimited: true });
        }

        // eslint-disable-next-line no-console
        console.error(e);
        return of({ serverError: true });
      }),
      first()
    );
  }
}
